import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { AlbumsService, Album, Image as AlbumImage } from '../../../services/albums.service';
import { FaCloudUploadAlt, FaCheck, FaTimes } from 'react-icons/fa';

const AlbumUploadPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [album, setAlbum] = useState<Album | null>(null);
    const [uploading, setUploading] = useState(false);
    const [files, setFiles] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState(0);

    useEffect(() => {
        if (id) {
            loadAlbum(id);
        }
    }, [id]);

    const loadAlbum = async (albumId: string) => {
        try {
            const data = await AlbumsService.findOne(albumId);
            setAlbum(data);
        } catch (error) {
            console.error('Failed to load album', error);
            navigate('/admin/albums');
        }
    };

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setFiles((prev) => [...prev, ...acceptedFiles]);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected: (fileRejections) => {
            fileRejections.forEach((rejection) => {
                const { file, errors } = rejection;
                const errorMessages = errors.map(e => {
                    if (e.code === 'file-too-large') return `Le fichier est trop volumineux (Max 8MB)`;
                    if (e.code === 'file-invalid-type') return `Type de fichier non supporté`;
                    return e.message;
                }).join(', ');
                alert(`Le fichier ${file.name} a été rejeté: ${errorMessages}`);
            });
        },
        accept: {
            'image/jpeg': ['.jpeg', '.jpg'],
            'image/png': ['.png'],
            'image/webp': ['.webp']
        },
        maxSize: 8 * 1024 * 1024, // 8MB
    });

    const handleUpload = async () => {
        if (!id || files.length === 0) return;

        setUploading(true);
        setUploadProgress(0);

        try {
            // Upload in chunks or all at once? The backend handles parsing, 
            // but showing individual progress requires separate requests or XHR tracking.
            // For simplicity/optimization requested, we'll send them in one batch 
            // but typically specialized uploaders do chunking.
            // Let's rely on the service.

            // Simulate progress for UX
            const interval = setInterval(() => {
                setUploadProgress(prev => Math.min(prev + 10, 90));
            }, 500);

            await AlbumsService.uploadImages(id, files);

            clearInterval(interval);
            setUploadProgress(100);

            // Reload album to show new images
            await loadAlbum(id);
            setFiles([]);
            setTimeout(() => setUploadProgress(0), 1000); // Reset progress

        } catch (error) {
            console.error('Upload failed', error);
            alert('Echec du téléchargement des images');
        } finally {
            setUploading(false);
        }
    };

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    if (!album) return <div className="p-8 text-center">Chargement...</div>;

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold mb-2">Ajouter des photos</h1>
                    <p className="text-gray-600">Album: {album.title}</p>
                </div>
                <button
                    onClick={() => navigate('/admin/albums')}
                    className="text-gray-600 hover:text-gray-800"
                >
                    Retour à la liste
                </button>
            </div>

            {/* Upload Zone */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors
                  ${isDragActive ? 'border-primary bg-primary-light' : 'border-gray-300 hover:border-primary'}
              `}
                >
                    <input {...getInputProps()} />
                    <FaCloudUploadAlt className="mx-auto text-4xl text-gray-400 mb-4" />
                    {isDragActive ? (
                        <p className="text-primary font-medium">Déposez les fichiers ici...</p>
                    ) : (
                        <div>
                            <p className="text-lg font-medium text-gray-700">Glissez-déposez vos photos ici</p>
                            <p className="text-sm text-gray-500 mt-2">ou cliquez pour sélectionner</p>
                            <p className="text-xs text-gray-400 mt-4">JPG, PNG, WebP acceptés (Max 8MB)</p>
                        </div>
                    )}
                </div>

                {/* Selected Files Preview */}
                {files.length > 0 && (
                    <div className="mt-6">
                        <h3 className="font-medium mb-3">Fichiers sélectionnés ({files.length})</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {files.map((file, index) => (
                                <div key={index} className="relative group bg-gray-50 rounded p-2 border">
                                    <div className="text-xs text-gray-600 truncate mb-1">{file.name}</div>
                                    <div className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                        <FaTimes size={10} />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 flex items-center gap-4">
                            <button
                                onClick={handleUpload}
                                disabled={uploading}
                                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {uploading ? 'Téléchargement...' : 'Envoyer les photos'}
                            </button>
                            {uploading && (
                                <div className="flex-1 max-w-xs bg-gray-200 rounded-full h-2.5">
                                    <div
                                        className="bg-primary h-2.5 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress}%` }}
                                    ></div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Existing Images */}
            <h2 className="text-xl font-bold mb-4">Photos existantes</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {album.images.map((img) => (
                    <div key={img.id} className="aspect-square relative group overflow-hidden rounded-lg bg-gray-100">
                        <img
                            src={img.thumb_url}
                            alt=""
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                            loading="lazy"
                        />
                    </div>
                ))}
                {album.images.length === 0 && (
                    <div className="col-span-full text-center py-8 text-gray-500 italic">
                        Aucune photo dans cet album.
                    </div>
                )}
            </div>

        </div>
    );
};

export default AlbumUploadPage;
